import React from 'react';
import PropTypes from 'prop-types';
import { LuMapPin, LuUserRound } from 'react-icons/lu';
import { RegionDot, StatusBadge, initialsOf, isInactive } from './centerBits';

const isBlank = (value) => value === undefined || value === null || value === '';
const MUTED = 'tw-text-slate-400 dark:tw-text-slate-500';

/** Names resolved by useCenters (SMO, backup SMO, region and its managers). */
export const lookupShape = PropTypes.shape({
  getSMOName: PropTypes.func.isRequired,
  getSMOBackupName: PropTypes.func.isRequired,
  getRegionForCenter: PropTypes.func.isRequired,
  getRegionManagerName: PropTypes.func.isRequired,
});

/** Tooltip text of a cell, as in the old table (region: "Bölge: sorumlular"). */
export const makeCellTitle = (lookup, label) => (center, key) => {
  if (key === 'SMO') return lookup.getSMOName(center) || undefined;
  if (key === 'Yedek SMO') return lookup.getSMOBackupName(center) || undefined;
  if (key === 'Bölge Müdürü') {
    const region = lookup.getRegionForCenter(center);
    if (!region) return label('table.noRegion');
    return label('table.regionTitle', { region: region.name, managers: lookup.getRegionManagerName(center) || label('table.noManager') });
  }
  if (key === 'Durum') return undefined;
  const value = center[key];
  return isBlank(value) || typeof value === 'object' ? undefined : String(value);
};

/** Content of one center table cell; inactive centers are drawn muted. */
const CenterCell = ({ columnKey, center, lookup }) => {
  const inactive = isInactive(center);
  const text = (value) => (isBlank(value)
    ? <span className={MUTED}>-</span>
    : <span className={`tw-block tw-truncate ${inactive ? MUTED : ''}`}>{String(value)}</span>);

  switch (columnKey) {
    case 'Adı':
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2.5">
          <span aria-hidden="true" className={`tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-[10px] tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${inactive ? 'tw-from-slate-300 tw-to-slate-500' : 'tw-from-sky-400/90 tw-to-blue-600/90'}`}>
            {initialsOf(center['Adı'])}
          </span>
          <span className={`tw-min-w-0 tw-truncate tw-font-semibold ${inactive ? 'tw-text-slate-500 dark:tw-text-slate-400' : 'tw-text-slate-900 dark:tw-text-white'}`}>
            {isBlank(center['Adı']) ? '-' : String(center['Adı'])}
          </span>
        </span>
      );
    case 'SMO':
      return text(lookup.getSMOName(center));
    case 'Yedek SMO':
      return text(lookup.getSMOBackupName(center));
    case 'Bölge Müdürü': {
      const region = lookup.getRegionForCenter(center);
      if (!region) return <span className={MUTED}>-</span>;
      const managers = lookup.getRegionManagerName(center);
      return (
        <span className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
          <RegionDot color={region.color} />
          <span className={`tw-min-w-0 tw-truncate ${inactive || !managers ? MUTED : ''}`}>{managers || '-'}</span>
        </span>
      );
    }
    case 'Durum':
      return <StatusBadge value={center['Durum']} />;
    default:
      return text(center[columnKey]);
  }
};

CenterCell.propTypes = { columnKey: PropTypes.string.isRequired, center: PropTypes.object.isRequired, lookup: lookupShape.isRequired };

/** One row of the card list (narrow screens). */
export const CenterCardRow = ({ center, lookup }) => {
  const inactive = isInactive(center);
  const region = lookup.getRegionForCenter(center);
  const smo = lookup.getSMOName(center);
  const place = [center['İl'], center['İlçe']].filter(Boolean).join(' / ');
  return (
    <>
      <span aria-hidden="true" className={`tw-grid tw-h-10 tw-w-10 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-gradient-to-br tw-text-xs tw-font-bold tw-text-white tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.45)] ${inactive ? 'tw-from-slate-300 tw-to-slate-500' : 'tw-from-sky-400 tw-to-blue-600'}`}>
        {initialsOf(center['Adı'])}
      </span>
      <span className="tw-min-w-0 tw-flex-1">
        <span className="tw-flex tw-min-w-0 tw-items-center tw-justify-between tw-gap-2">
          <span className={`tw-min-w-0 tw-truncate tw-text-[15px] tw-font-semibold ${inactive ? 'tw-text-slate-500 dark:tw-text-slate-400' : 'tw-text-slate-900 dark:tw-text-white'}`}>{center['Adı'] || '-'}</span>
          <StatusBadge value={center['Durum']} />
        </span>
        {place && (
          <span className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1 tw-text-[13px] tw-text-slate-500 dark:tw-text-slate-400">
            <LuMapPin aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
            <span className="tw-truncate">{place}</span>
          </span>
        )}
        {(smo || region) && (
          <span className="tw-mt-1.5 tw-flex tw-min-w-0 tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {smo && (
              <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1">
                <LuUserRound aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0" />
                <span className="tw-truncate">{smo}</span>
              </span>
            )}
            {region && (
              <span className="tw-inline-flex tw-min-w-0 tw-items-center tw-gap-1.5">
                <RegionDot color={region.color} className="tw-h-2 tw-w-2" />
                <span className="tw-truncate">{region.name}</span>
              </span>
            )}
          </span>
        )}
      </span>
    </>
  );
};

CenterCardRow.propTypes = { center: PropTypes.object.isRequired, lookup: lookupShape.isRequired };

export default CenterCell;
